<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <template #head>
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h2 class="opc-zm-base-card__name text-capitalize">
              <span :class="[isAdd2CartOrEditFlow ? 'opc-addon-config__title--add' : '']">{{
                title }}</span>
            </h2>
            <div v-if="cmrUsedValue?.overage > 0 && isExpanded"
              class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <span class="opc-addon-config__subtitle-current">{{ $t('billing.cmr.current_usage') }}:</span>
              <span class="opc-addon-config__subtitle-used">{{ cmrUsedDesc.used }}</span>
              <span class="opc-addon-config__subtitle-overage">{{ cmrUsedDesc.overage }}</span>
              <zm-tooltip class="opc-addon-config-row__input__number--tips"
                disallow-learn-about
                :aria-label="`Learn more about ${cmrUsedValue?.overage || 0} GB Over capacity`" placement="bottom">
                <template #content>
                <div v-html="tips" />
                </template>
                <tips-icon type="info" :aria-label="`Learn more about ${cmrUsedValue?.overage || 0} GB Over capacity`" />
              </zm-tooltip>
            </div>
            <div v-if="hasSelectedPlan && !isExpanded && isAdd2CartOrEditFlow"
              class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <storage-content :selected-plan="selectedPlan" />
            </div>
            <div v-if="!isAdd2CartOrEditFlow && !isExpanded" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <storage-content v-if="hasSelectedPlan" :selected-plan="selectedPlan" />
            </div>
          </div>
        </div>
      </template>
      <div class="divide-line"></div>
      <div class="opc-addon-config__main">
        <p class="opc-addon-config__main__title">{{ desc }}</p>
        <zm-radio-group v-model="selectedPlanCode" @update:model-value="onPlanSelect" ref="radioGroup"
          aria-label="Select a capacity" class="opc-addon-config__main__plan opc-card-radio">
          <zm-radio v-for="plan in visiblePlanList" :data-testid="`checkout-radio-${plan.plan_code}`" :key="plan.plan_code" :label="plan.plan_code" :disabled="calculateIsOptionDisabled(plan)">
            <p class="opc-addon-config__main__plan__capacity">
              <span>{{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}</span>
              <CycleCurrentBadge :value="plan" />
              <span v-if="recommendedCapacity.category === plan.category"
                class="opc-addon-config__main__plan__capacity-recommend">
                {{ $t('billing.cmr.recommended') }}
              </span>
            </p>
            <div class="opc-addon-config__main__plan__price">
              <p class="opc-addon-config__main__plan__price--amount">{{ getFormattedPriceOfMonthWithDownsell(plan, { forceDiff: true })
              }}</p>
              <p class="opc-addon-config__main__plan__price--unit">
                <span v-html="getMergedBilledText(plan.billing_cycle, plan.amount, planUnitMap.credits)"></span><priceVATTip />
              </p>
            </div>
          </zm-radio>
        </zm-radio-group>
        <zm-banner show-close v-if="willIncurOverage" class="opc-common-info-alert" type="warning" show-icon :title="$t('billing.cmr.option_incur_overage')">
          <p v-html="willIncurOverage"></p>
        </zm-banner>
        <div class="opc-zm-base-card__main__footer">
          <p class="opc-addon-config__main__footer--tips">
            <span>*{{ $t('billing.cmr.overage_fees_desc') + ' ' + $t('billing.cmr.fees_vary_based_on_storage_plan')
            }}</span>
            <span v-for="(plan, index) in visiblePlanList" :key="index">
              {{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}:
              {{ $t('billing.cmr.usage_in_excess', [plan.excessPrice]) }};
            </span>
            <a data-testid="checkout-a-45" href="https://support.zoom.us/hc/en-us/articles/360060661511-Cloud-recording-storage-capacity#h_01F4HFK8CMSEBNQZ5J624FHHW7"
              target="_blank">{{ $t('billing.common.Learn_more') }}</a>
          </p>
          <div class="opc-zm-base-card__btn--wrap">
            <zm-button data-testid="checkout-button-46" v-if="isShowRemoveBtn" type="text" @click="removePlanByKey"
              :aria-label="`remove ${title} plan`">{{ $t('billing.common.remove') }}</zm-button>
          </div>
        </div>
        <preview-payment class="opc-addon-config__preview-payment" />
      </div>
    </foldable>
  </tile>
</template>

<script>
import { get } from "lodash";
import CMRCommon from './mixin';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import StorageContent from './components/storage-content.vue';
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { planTypes, getMeetingDefaultCMRStorage, MRS_STATUS_TYPE_MAP } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { translation } from '@/plugins/i18n';
import { transferMoney } from '@/utils/money';
import { handleRecommended, getExcessPriceCents, UNLIMITED_CMR } from './constants';
import BillingMessageBox from '@/components/common/message-box';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import baseplanFreeToPaidReplenishMixin from "@/components/capacity/mx/freetrial-baseplan-replenish";
import CycleCurrentBadge from '@/components/service/config-billing-cycle/cycle-current-badge.vue';
import autofill from "@/plans/mx/autofill";
import discountedMonthPrice from '@/common/mixin/discounted-month-price';
import {planUnitMap} from "@/common/product-unit";
import { checkMRSChange } from '@/utils/validator';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'CMR-Config',
  mixins: [baseplanFreeToPaidReplenishMixin, CMRCommon, plansMixin, autofill, discountedMonthPrice],
  components: {
    PreviewPayment,
    Foldable,
    Tile,
    StorageContent,
    CycleCurrentBadge,
  },
  data() {
    return {
      isExpanded: true,
      billingCycle: '',
      selectedPlanCode: '',
      prevSelectedPlanCode: '',
      planType: planTypes.CMR,
    };
  },
  computed: {
    planUnitMap() {
      return planUnitMap;
    },
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['getSelectedPlans', 'basicConfig', 'cmrUsedValue', 'getCurrentMeetingPlans', 'currentEditPlan']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === planTypes.CMR);
    },
    visiblePlanList() {
      return this.planList.filter((p) => p.isVisible === true);
    },
    freeStorage() {
      return this.basicConfig?.freeStorage || '';
    },
    selectedPlan() {
      return this.planList.find((item) => item.plan_code === this.selectedPlanCode);
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === planTypes.CMR), [0]);
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    desc() {
      if (!!this.freeStorage && this.freeStorage !== UNLIMITED_CMR && this.freeStorage !== '0') {
        if (this.cmrUsedValue.overage > 0) {
          return translation('billing.cmr.add_desc');
        }
        return translation('billing.cmr.account_cloud_storage_desc', [this.freeStorage]) + ' ' + translation('billing.cmr.add_desc');
      } else if (this.freeStorage === UNLIMITED_CMR) {
        return '';
      } else {
        const totalCapacity = this.getCurrentMeetingPlans.reduce((sum, p) => {
          const licenseQuantity = get(p, 'cartInfo.selected_number', 0);
          return sum + licenseQuantity * getMeetingDefaultCMRStorage(p, 0);
        }, 0);
        return this.$t('billing.cmr.config_desc', { totalCapacity });
      }
    },
    cmrUsedDesc() {
      const { used, total, overage, overageWithUnit } = this.cmrUsedValue;
      return {
        used: used ? `${used} GB of ${total} GB` : '',
        overage: overage ? `(${overageWithUnit} over capacity)` : ''
      }
    },
    recommendedCapacity() {
      if (this.cmrUsedValue.overage > 0) {
        // find first value to meet this condition or the last one
        return handleRecommended(this.planList, this.cmrUsedValue.overage);
      }
      return {};
    },
    willIncurOverage() {
      if (this.cmrUsedValue.overage > 0 && this.selectedPlan.capacity < this.cmrUsedValue.overage) {
        const overage = this.cmrUsedValue.overage - this.selectedPlan.capacity;
        const money = transferMoney(getExcessPriceCents(this.selectedPlan.excessPriceCents || this.selectedPlan.excessPrice) * overage);
        return this.$t('billing.cmr.selection_will_incur_overage_desc', [`<b style="font-weight: 600">${money}</b>`]);
      }
      return '';
    },
    tips() {
      return this.$t('billing.cmr.account_cloud_storage_include_desc', [`${this.cmrUsedValue.total} GB`]);
    },
    disableDownSell() {
      return this.basicConfig.downSellData.disableDownSell;
    },
  },
  mounted() {
    const freeUpgradeTargetPlan = this.planList.find(p => p.old_plan_code === this.selectedPlanCode)
    if(freeUpgradeTargetPlan){
      if (this.selectedPlanCode !== freeUpgradeTargetPlan.plan_code) {
        // save the previous selected plan code for capacity migration
        this.$context?.is?.edit && (this.prevSelectedPlanCode = this.selectedPlanCode);
        this.onPlanSelect(freeUpgradeTargetPlan.plan_code);
        if(this.$query?.upgrade_now !== 'true'){
          let newCapacity = `${freeUpgradeTargetPlan.cartCapacity}${freeUpgradeTargetPlan.cartCapacityUnit}`;
          BillingMessageBox.alert(
            this.$t('billing.cmr.cmr_free_upgrade.update_tip', [newCapacity]),
            this.$t('billing.cmr.cmr_free_upgrade.update_title'),
          );
        }
      }
    }
    this.$context?.$on('collapse-plan-card', this.collapseCard);
    this.prevSelectedPlanCode = this.selectedPlanCode;
  },
  beforeUnmount() {
    // Vue 3: Clean up event listeners
    this.$context?.$off('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.prevSelectedPlanCode;

      if (oldPlanCode !== newPlanCode) {
        this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
      } else {
        this.updateSelectPlan({
          plan_code: newPlanCode,
          cartInfo: { selected_number: 1 },
        });
      }

      const plan = this.planList?.find((plan) => plan.plan_code === newPlanCode);
      plan && this.$tracking.addOnBuyInteraction(getTrackingProductNameByPlanType(planTypes.CMR), `${plan?.cartCapacity}${plan?.cartCapacityUnit}`);
      this.prevSelectedPlanCode = newPlanCode;
    },
    onPlanRemove() {
      this.removePlan(this.selectedPlanCode);
      this.selectedPlanCode = '';
      this.prevSelectedPlanCode = '';
    },
    // For A11Y
    getFirstFocusableControl() {
      // Vue 3: $refs is now reactive, so we need to check if it exists
      if (this.$refs.radioGroup && this.$refs.radioGroup.$el) {
        let firstRadioEl = this.$refs.radioGroup.$el.querySelector('.zbo-radio').querySelector('input[type="radio"]');
        return Promise.resolve(firstRadioEl);
      }
      return Promise.resolve(null);
    },
    collapseCard() {
      this.isExpanded = false;
    },
    calculateIsOptionDisabled(plan) {
      if (this.disableDownSell) {
        return checkMRSChange({
          oldPlan: this.currentEditPlan,
          nextPlan: plan,
          nextPlanQuantity: 1
        }) === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE
      }
      return false
    }
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction('cloud storage accordion', val ? 'expand' : 'collapse');
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';
@import '@/styles/plans/addon-config.scss';
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

.opc-addon-config {
  // &__title {
  //   font-weight: 500;
  //   font-size: 14px;
  //   color: #515079;

  //   &-content {
  //     display: flex;
  //     flex-direction: column;
  //     align-items: flex-start;
  //     justify-content: center;
  //   }
  // }

  .opc-addon-config__subtitle {
    display: flex;
    align-items: center;

    ul {
      margin-left: 16px;
      display: flex;
      align-items: center;

      li span,
      li label {
        font-size: 14px;
        font-weight: 500;
        line-height: 120%;
        margin-left: 0;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      }

      &>li:not(:first-child)::before {
        content: "\2022";
        margin: 0 10px;
      }
    }

    &-current {
      color: var(--zoom-color-text-neutral, #686F79);
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      margin-right: 2px;
      text-transform: initial;
    }

    &-used {
      color: var(--zoom-color-text-strong-neutral, #555B62);
      margin-left: 0;
      margin-right: 2px;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }

    &-overage {
      color: var(--zoom-color-text-warning, #B36200);;
      margin: 0;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }
  }

  &__main {
    &__title {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
    }
    &__plan {
      display: flex;
      flex-wrap: wrap;
      margin-top: 24px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

      &.opc-card-radio .zbo-radio {
        flex: 0 0 calc(50% - 8px);

        &__wrap {
          width: 100%;
        }

        &__label {
          &--inner {
            width: 100%;
          }

          &.is-disabled {

            .opc-addon-config__main__plan__price--amount,
            .opc-addon-config__main__plan__price--unit {
              color: var(--zoom-color-state-disable, #ADB1B8);
            }
          }
        }
      }

      &__capacity,
      &__price {
        line-height: 150%;
      }

      &__capacity {
        font-size: 16px;
        display: flex;
        align-items: center;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        font-style: normal;
        font-weight: 590;
        line-height: 20px;

        &-recommend {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(96.59deg, #084845 10.36%, #09A639 89.64%);
          border-radius: 100px;
          padding: 0px 8px;
          color: #FFFFFF;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          margin-left: 8px;
        }
      }

      &__price {
        font-weight: 450;
        font-size: 22px;
        word-break: break-word;

        &--amount {
          font-size: 24px;
          font-style: normal;
          font-weight: 590;
          line-height: 28px;
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
          margin: 4px 0 2px;
        }

        &--unit {
          font-size: 14px;
          font-weight: 400;
          line-height: 18px;
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        }
      }
    }

    &__footer {
      margin-top: 16px;

      &--tips {
        font-weight: 400;
        font-size: 12px;
        color: var(--zoom-color-text-neutral, #686F79);
        text-align: left;
        line-height: 16px;
        a {
          color: var(--zoom-color-text-primary, #0D6BDE);
        }
      }
    }
  }

  &__btn {
    &--wrap {
      margin-top: 24px;
    }
  }

  .opc-common-info-alert {
    margin-top: 24px;
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
